import React, { useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import api from "../api/client";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import {
  Database, Download, FileSpreadsheet, HardDrive, Trash2, Loader2, Upload,
  CheckCircle2, AlertTriangle, Building2, Users, FileText, Wallet, Archive,
} from "lucide-react";

const FORMAT_BYTES = (n) => {
  if (!n) return "—";
  if (n > 1024 * 1024 * 1024) return (n / 1024 / 1024 / 1024).toFixed(2) + " Go";
  if (n > 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + " Mo";
  return Math.round(n / 1024) + " Ko";
};

const EXPORTS = [
  { key: "clients", label: "Clients & Prospects", icon: Users, route: "/backup/export/clients.xlsx" },
  { key: "contrats", label: "Contrats", icon: FileText, route: "/backup/export/contrats.xlsx" },
  { key: "paiements", label: "Paiements", icon: Wallet, route: "/backup/export/paiements.xlsx" },
  { key: "immeubles", label: "Projets immobiliers", icon: Building2, route: "/backup/export/immeubles.xlsx" },
];

export default function Sauvegardes() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [includeUploads, setIncludeUploads] = useState(true);
  const [persistOnDisk, setPersistOnDisk] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backup/list");
      setBackups(data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await api.post(
        "/backup/global",
        null,
        {
          params: { include_uploads: includeUploads, persist: persistOnDisk },
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "").slice(0, 15);
      a.href = url;
      a.download = `tpco_backup_${stamp}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée");
      if (persistOnDisk) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const downloadExport = async (item) => {
    setExporting(item.key);
    try {
      const res = await api.get(item.route, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "").slice(0, 15);
      a.href = url;
      a.download = `tpco_${item.key}_${stamp}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch {
      toast.error("Erreur d'export");
    } finally {
      setExporting(null);
    }
  };

  const downloadOld = async (b) => {
    try {
      const res = await api.get(`/backup/download/${b.filename}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur de téléchargement");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Supprimer définitivement ${b.filename} ?`)) return;
    try {
      await api.delete(`/backup/${b.filename}`);
      toast.success("Sauvegarde supprimée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const importClients = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backup/import/clients", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.created} créés · ${data.updated} mis à jour`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-[1300px] mx-auto">
        <PageHeader
          kicker="Administration · Données"
          title="Sauvegardes & Exports"
          subtitle="Téléchargez une copie complète du système, ou exportez chaque module en Excel."
        />

        {/* === SAUVEGARDE GLOBALE === */}
        <div className="tpco-card p-6 mb-6" data-testid="backup-global">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-11 h-11 rounded-md bg-[#E8EBFA] text-[#1E3A8A] flex items-center justify-center shrink-0">
              <Database className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold text-[#0F1A3D]">Sauvegarde globale</h3>
              <p className="text-xs text-[#4B5775] mt-0.5">Génère un ZIP complet de la base MongoDB + tous les fichiers uploadés (CNI, contrats, photos, 3D, vidéos).</p>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <label className="flex items-center gap-3 cursor-pointer text-sm">
              <input type="checkbox" checked={includeUploads} onChange={(e) => setIncludeUploads(e.target.checked)} className="w-4 h-4" data-testid="bk-include-uploads" />
              <span className="text-[#0F1A3D]"><strong>Inclure les fichiers uploadés</strong> (CNI, contrats, médias) — recommandé</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer text-sm">
              <input type="checkbox" checked={persistOnDisk} onChange={(e) => setPersistOnDisk(e.target.checked)} className="w-4 h-4" data-testid="bk-persist" />
              <span className="text-[#0F1A3D]"><strong>Conserver sur le serveur</strong> (en plus du téléchargement)</span>
            </label>
          </div>

          <button
            onClick={createBackup}
            disabled={creating}
            className="h-11 px-5 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="bk-create"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {creating ? "Génération…" : "Lancer la sauvegarde"}
          </button>

          {/* List of persisted backups */}
          {backups.length > 0 && (
            <div className="mt-6 border-t border-[#E2E5F1] pt-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#4B5775] mb-3">Sauvegardes sur le serveur · {backups.length}</div>
              <div className="space-y-1.5">
                {backups.map((b) => (
                  <div key={b.filename} className="flex items-center gap-3 px-3 py-2 rounded-md border border-[#E2E5F1] hover:bg-[#F8FAFC]" data-testid={`bk-${b.filename}`}>
                    <HardDrive className="w-4 h-4 text-[#1E3A8A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs font-bold text-[#0F1A3D]">{b.filename}</div>
                      <div className="text-[10px] text-[#4B5775]">{fmtDate(b.created_at)} · {FORMAT_BYTES(b.size)}</div>
                    </div>
                    <button onClick={() => downloadOld(b)} className="w-8 h-8 rounded-md border border-[#E2E5F1] hover:border-[#1E3A8A] hover:text-[#1E3A8A] text-[#4B5775] inline-flex items-center justify-center"><Download className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(b)} className="w-8 h-8 rounded-md border border-[#E2E5F1] hover:border-[#C83B3B] hover:text-[#C83B3B] text-[#4B5775] inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* === EXPORTS EXCEL === */}
        <div className="tpco-card p-6 mb-6" data-testid="backup-exports">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-11 h-11 rounded-md bg-[#E5F0E8] text-[#2D7A3F] flex items-center justify-center shrink-0">
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-[#0F1A3D]">Exports Excel par module</h3>
              <p className="text-xs text-[#4B5775] mt-0.5">Téléchargez vos données dans un format ouvert et éditable.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EXPORTS.map((it) => {
              const Ic = it.icon;
              const isExporting = exporting === it.key;
              return (
                <button
                  key={it.key}
                  onClick={() => downloadExport(it)}
                  disabled={isExporting}
                  className="border border-[#E2E5F1] hover:border-[#1E3A8A] hover:bg-[#E8EBFA]/40 rounded-lg p-4 flex items-center gap-3 text-left transition disabled:opacity-50"
                  data-testid={`bk-export-${it.key}`}
                >
                  <Ic className="w-7 h-7 text-[#1E3A8A] shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#0F1A3D]">{it.label}</div>
                    <div className="text-[11px] text-[#4B5775] mt-0.5">.xlsx — Téléchargement direct</div>
                  </div>
                  {isExporting ? <Loader2 className="w-4 h-4 animate-spin text-[#1E3A8A]" /> : <Download className="w-4 h-4 text-[#4B5775]" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* === IMPORT CLIENTS === */}
        <div className="tpco-card p-6" data-testid="backup-import">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-md bg-[#FFF4E5] text-[#7A5A1A] flex items-center justify-center shrink-0">
              <Upload className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-[#0F1A3D]">Import clients (Excel)</h3>
              <p className="text-xs text-[#4B5775] mt-0.5">Réimportez un fichier exporté ou un fichier Excel respectant les colonnes <em>ID, Type, Civilité, Nom, Prénom, Raison sociale, Téléphone, Email, Adresse, Ville, Pays</em>. Le matching se fait sur email ou téléphone.</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input ref={importRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => importClients(e.target.files?.[0])} />
            <button
              onClick={() => importRef.current?.click()}
              disabled={importing}
              className="h-11 px-5 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="bk-import-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Sélectionner un fichier .xlsx
            </button>
          </div>

          {importResult && (
            <div className="mt-4 p-3 rounded-md bg-[#E5F0E8] border border-[#B9D8C2] text-xs text-[#2D5A3C]" data-testid="bk-import-result">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#2D7A3F] mt-0.5" />
                <div>
                  <strong>{importResult.created} clients créés</strong> · {importResult.updated} mis à jour
                  {importResult.errors?.length > 0 && (
                    <div className="text-[#7A2D2D] mt-1 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <div>{importResult.errors.length} erreur{importResult.errors.length > 1 ? "s" : ""} : {importResult.errors[0]}…</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
